import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ShoppingBag } from "lucide-react";

const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const isToday = (iso) => new Date(iso).toDateString() === new Date().toDateString();

export const ConsumptionHistory = ({ items, empty = "Nenhuma movimentação ainda.", testId = "consumption-history", maxH = "max-h-56" }) => (
  items.length === 0 ? (
    <p className="text-xs text-muted-foreground" data-testid="consumption-empty">{empty}</p>
  ) : (
    <div className={`space-y-1 ${maxH} overflow-y-auto`} data-testid={testId}>
      {items.map((e) => (
        <div key={e.id} className="flex justify-between gap-3 text-xs py-1.5 border-b border-border/50" data-testid={`consumption-item-${e.id}`}>
          <div className="min-w-0">
            <div className="truncate">{e.description}</div>
            <div className="text-muted-foreground">{new Date(e.created_at).toLocaleString("pt-BR")}</div>
          </div>
          <div className={`font-mono shrink-0 ${e.type === "debit" ? "text-rose-500" : "text-emerald-500"}`}>
            {e.type === "debit" ? "−" : "+"} {brl(e.amount)}
          </div>
        </div>
      ))}
    </div>
  )
);

export const ConsumptionCard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [unread, setUnread] = useState(0);
  const [full, setFull] = useState(false);
  useEffect(() => {
    api.get("/consumption").then((r) => setData(r.data)).catch(() => setData({ balance: 0, items: [] }));
    api.get("/store/orders").then((r) => setUnread(r.data.reduce((a, o) => a + (o.unread_user || 0), 0))).catch(() => {});
  }, []);
  if (!data) return null;
  return (
    <Card className="p-6 mt-6 border-emerald-500/40 bg-emerald-500/5" data-testid="card-consumption">
      {unread > 0 && (
        <button type="button" onClick={() => navigate("/pedidos")} className="w-full mb-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/40 text-sm text-left" data-testid="orders-unread-notice">
          Você tem <b>{unread}</b> mensagem(ns) nova(s) sobre seus pedidos. Clique para responder.
        </button>
      )}
      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-1/3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-bold text-emerald-500">
            <ShoppingBag className="h-4 w-4" /> SALDO CONSUMO
          </div>
          <div className="mt-2 font-mono text-3xl font-bold" data-testid="consumption-balance">{brl(data.balance)}</div>
          <p className="text-xs text-muted-foreground mt-2">
            20% de cada comissão. Não pode ser sacado: use na compra de produtos e serviços da plataforma.
          </p>
        </div>
        <div className="md:flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Movimentações de hoje</div>
            <Button variant="link" className="px-0 h-auto text-xs text-emerald-500" onClick={() => setFull(true)} data-testid="consumption-full-history-link">
              Ver histórico completo
            </Button>
          </div>
          <ConsumptionHistory items={data.items.filter((e) => isToday(e.created_at))} empty="Nenhuma movimentação hoje." />
        </div>
      </div>
      <Dialog open={full} onOpenChange={setFull}>
        <DialogContent className="max-w-lg" data-testid="consumption-full-history-dialog">
          <DialogHeader><DialogTitle>Histórico completo do Saldo Consumo</DialogTitle></DialogHeader>
          <ConsumptionHistory items={data.items} testId="consumption-full-history" maxH="max-h-[60vh]" />
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export const AdminConsumptionPanel = ({ userId, onChanged }) => {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/users/${userId}/consumption`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const debit = async () => {
    try {
      await api.post(`/admin/users/${userId}/consumption/debit`, { amount: Number(amount.replace(",", ".")), reason });
      toast.success("Saldo Consumo descontado");
      setAmount(""); setReason("");
      load(); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return null;
  return (
    <div className="p-3 rounded-xl border border-emerald-500/40" data-testid="admin-consumption-panel">
      <div className="flex justify-between items-center">
        <div className="text-xs uppercase font-bold tracking-wider text-emerald-500">Saldo Consumo</div>
        <div className="font-mono font-bold" data-testid="admin-consumption-balance">{brl(data.balance)}</div>
      </div>
      <div className="flex gap-2 mt-3">
        <Input placeholder="Valor" inputMode="decimal" className="w-24" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="consumption-debit-amount" />
        <Input placeholder="Motivo (ex.: compra de produto)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="consumption-debit-reason" />
        <Button size="sm" variant="outline" disabled={!amount || reason.trim().length < 3} onClick={debit} data-testid="consumption-debit-btn">Descontar</Button>
      </div>
      <div className="mt-3"><ConsumptionHistory items={data.items} /></div>
    </div>
  );
};
